// API Service for Elasticsearch Logs
const DEFAULT_API_URL =
  process.env.REACT_APP_API_URL || "https://activdev.nosylabs.fr";
// const DEFAULT_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003';
const API_URL_STORAGE_KEY = "logs_dashboard_api_url";

export const getApiUrl = (): string =>
  localStorage.getItem(API_URL_STORAGE_KEY) || DEFAULT_API_URL;

export const setApiUrl = (url: string): void => {
  const trimmed = url.replace(/\/+$/, "");
  localStorage.setItem(API_URL_STORAGE_KEY, trimmed);
};

export const resetApiUrl = (): void => {
  localStorage.removeItem(API_URL_STORAGE_KEY);
};

export interface LogEntry {
  id?: string;
  timestamp: string;
  level: "error" | "warn" | "info" | "debug";
  message: string;
  service: string;
  environment?: string;
  source?: string;
  userId?: string;
  requestId?: string;
  ip?: string;
  method?: string;
  path?: string;
  statusCode?: number;
  responseTime?: number;
  metadata?: Record<string, any>;
  tags?: string[];
  stacktrace?: string;
}

export interface LogsResponse {
  success: boolean;
  data: {
    grouped?: boolean;
    groupBy?: string;
    logs?: LogEntry[];
    groups?: Array<{
      userId: string;
      totalLogs: number;
      firstLogTimestamp?: string;
      levels: Array<{ key: string; doc_count: number }>;
      services: Array<{ key: string; doc_count: number }>;
      logs: LogEntry[];
    }>;
    total: number;
    page: number;
    pages: number;
  };
}

export interface StatsOverview {
  success: boolean;
  data: {
    total: number;
    byLevel: Array<{ key: string; doc_count: number }>;
    byService: Array<{ key: string; doc_count: number }>;
    byHour: Array<{ key: number; key_as_string: string; doc_count: number }>;
    errorCount: number;
    byUser?: Array<{ key: string; doc_count: number; name?: string | null }>;
  };
}

export interface TimelineData {
  success: boolean;
  data: Array<{
    key: number;
    key_as_string: string;
    doc_count: number;
    by_level?: {
      buckets: Array<{ key: string; doc_count: number }>;
    };
    by_service?: {
      buckets: Array<{ key: string; doc_count: number }>;
    };
  }>;
}

export interface ServiceInfo {
  name: string;
  total: number;
  levels: Record<string, number>;
  errorCount: number;
  errorRate: string;
  lastActivity: string;
  avgResponseTime: number | null;
}

export interface ServiceDetail extends ServiceInfo {
  byHour: Array<{ key: number; key_as_string: string; doc_count: number }>;
  recentLogs: LogEntry[];
}

export interface Comment {
  id: string;
  service: string;
  author: string;
  content: string;
  timestamp: string;
}

export interface ReportSummary {
  total: number;
  errors: number;
  warnings: number;
  errorRate: string;
  avgResponseTime: number | null;
  serviceCount: number;
}

export interface ReportData {
  summary: ReportSummary;
  levels: Record<string, number>;
  services: Array<{
    name: string;
    total: number;
    errors: number;
    errorRate: string;
    avgResponse: number | null;
  }>;
  daily: Array<{
    date: string;
    total: number;
    levels: Record<string, number>;
  }>;
  topErrors: Array<{ message: string; count: number }>;
  topEndpoints: Array<{ path: string; count: number }>;
}

export interface Report {
  id: string;
  title: string;
  description: string;
  type: string;
  period: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  author: string;
  status: string;
  data: ReportData;
}

class LogsApiService {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
    localStorage.setItem("logs_dashboard_token", token);
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem("logs_dashboard_token");
    }
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem("logs_dashboard_token");
  }

  private async fetch(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<any> {
    const url = `${getApiUrl()}${endpoint}`;
    const token = this.getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...((options.headers as Record<string, string>) || {}),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401 || response.status === 403) {
      window.dispatchEvent(new CustomEvent("auth:expired"));
      throw new Error("Session expirée, veuillez vous reconnecter");
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // Health check
  async healthCheck(): Promise<{ status: string; elasticsearch: string }> {
    return this.fetch("/api/health");
  }

  // Get logs with filters
  async getLogs(params: {
    q?: string;
    level?: string;
    service?: string;
    excludeService?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sort?: "asc" | "desc";
    ip?: string;
    source?: string;
    groupBy?: string;
  }): Promise<LogsResponse> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        queryParams.append(key, String(value));
      }
    });

    return this.fetch(`/api/logs?${queryParams.toString()}`);
  }

  // Get stats overview
  async getStatsOverview(
    startDate?: string,
    endDate?: string,
    ip?: string,
    source?: string,
  ): Promise<StatsOverview> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (ip) params.append("ip", ip);
    if (source) params.append("source", source);
    return this.fetch(`/api/stats/overview?${params}`);
  }

  // Get timeline
  async getTimeline(
    interval: string = "1h",
    startDate?: string,
    endDate?: string,
    ip?: string,
    source?: string,
  ): Promise<TimelineData> {
    const params = new URLSearchParams();
    params.append("interval", interval);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (ip) params.append("ip", ip);
    if (source) params.append("source", source);
    return this.fetch(`/api/stats/timeline?${params}`);
  }

  // Get available levels
  async getLevels(): Promise<{
    success: boolean;
    data: Array<{ key: string; doc_count: number }>;
  }> {
    return this.fetch("/api/logs/levels");
  }

  // Get available services. Optional filters narrow the counts (but never the
  // list of services) so dropdowns reflect the user's current selection.
  async getServices(filters?: {
    q?: string;
    level?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    success: boolean;
    data: Array<{ key: string; doc_count: number }>;
  }> {
    const params = new URLSearchParams();
    if (filters?.q) params.set("q", filters.q);
    if (filters?.level) params.set("level", filters.level);
    if (filters?.startDate) params.set("startDate", filters.startDate);
    if (filters?.endDate) params.set("endDate", filters.endDate);
    const qs = params.toString();
    return this.fetch(`/api/logs/services${qs ? `?${qs}` : ""}`);
  }

  // Get top errors
  async getTopErrors(size: number = 10): Promise<{
    success: boolean;
    data: Array<{
      key: string;
      doc_count: number;
      latest: { hits: { hits: Array<{ _source: LogEntry }> } };
    }>;
  }> {
    return this.fetch(`/api/stats/top-errors?size=${size}`);
  }

  // Ingest single log
  async ingestLog(
    log: Partial<LogEntry>,
  ): Promise<{ success: boolean; data: LogEntry }> {
    return this.fetch("/api/logs", {
      method: "POST",
      body: JSON.stringify(log),
    });
  }

  // Ingest batch logs
  async ingestBatchLogs(
    logs: Partial<LogEntry>[],
  ): Promise<{ success: boolean; data: { ingested: number; errors: number } }> {
    return this.fetch("/api/logs/batch", {
      method: "POST",
      body: JSON.stringify({ logs }),
    });
  }

  // Delete log
  async deleteLog(id: string): Promise<{ success: boolean; message: string }> {
    return this.fetch(`/api/logs/${id}`, {
      method: "DELETE",
    });
  }

  // ── Services ──

  async getServicesList(): Promise<{ success: boolean; data: ServiceInfo[] }> {
    return this.fetch("/api/services");
  }

  async getServiceDetail(
    name: string,
  ): Promise<{ success: boolean; data: ServiceDetail }> {
    return this.fetch(`/api/services/${encodeURIComponent(name)}`);
  }

  // ── Comments ──

  async getComments(
    service: string,
  ): Promise<{ success: boolean; data: Comment[] }> {
    return this.fetch(`/api/services/${encodeURIComponent(service)}/comments`);
  }

  async addComment(
    service: string,
    author: string,
    content: string,
  ): Promise<{ success: boolean; data: Comment }> {
    return this.fetch(`/api/services/${encodeURIComponent(service)}/comments`, {
      method: "POST",
      body: JSON.stringify({ author, content }),
    });
  }

  async deleteComment(
    service: string,
    commentId: string,
  ): Promise<{ success: boolean; message: string }> {
    return this.fetch(
      `/api/services/${encodeURIComponent(service)}/comments/${commentId}`,
      {
        method: "DELETE",
      },
    );
  }

  // ── Reports ──

  async getReports(): Promise<{ success: boolean; data: Report[] }> {
    return this.fetch("/api/reports");
  }

  async getReport(id: string): Promise<{ success: boolean; data: Report }> {
    return this.fetch(`/api/reports/${id}`);
  }

  async generateReport(params: {
    title?: string;
    description?: string;
    type?: string;
    period?: string;
    startDate: string;
    endDate: string;
    author?: string;
  }): Promise<{ success: boolean; data: Report }> {
    return this.fetch("/api/reports/generate", {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  async deleteReport(
    id: string,
  ): Promise<{ success: boolean; message: string }> {
    return this.fetch(`/api/reports/${id}`, {
      method: "DELETE",
    });
  }

  // ── Platform Statistics ──

  async getPlatformStats(
    startDate?: string,
    endDate?: string,
    ip?: string,
    source?: string,
  ): Promise<{
    success: boolean;
    data: {
      total: number;
      platforms: Array<{
        platform: string;
        count: number;
        percentage: string;
        byLevel: Array<{ key: string; doc_count: number }>;
        byService: Array<{ key: string; doc_count: number }>;
        timeline: Array<{
          key: number;
          key_as_string: string;
          doc_count: number;
          by_service?: { buckets: Array<{ key: string; doc_count: number }> };
        }>;
      }>;
      summary: {
        admin: {
          count: number;
          percentage: string;
          levels: Array<{ key: string; doc_count: number }>;
        };
        web: {
          count: number;
          percentage: string;
          levels: Array<{ key: string; doc_count: number }>;
        };
        android: {
          count: number;
          percentage: string;
          levels: Array<{ key: string; doc_count: number }>;
        };
        ios: {
          count: number;
          percentage: string;
          levels: Array<{ key: string; doc_count: number }>;
        };
      };
      servicesByDay: Array<{
        key: number;
        key_as_string: string;
        doc_count: number;
        by_service: { buckets: Array<{ key: string; doc_count: number }> };
      }>;
    };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (ip) params.append("ip", ip);
    if (source) params.append("source", source);
    return this.fetch(`/api/stats/platforms?${params}`);
  }
}

export const logsApi = new LogsApiService();
